/**
 * DocumentUploader.jsx — shared dropzone used by Tendering, Project Planning
 * and (forthcoming) BD Opportunities so the upload UX stays identical and
 * only ONE component carries the dropzone, category pill, and uploaded-list
 * implementation.
 *
 * Previously this code was duplicated inline in:
 *   - frontend/src/pages/crm/EnquiryDetail.jsx
 *   - frontend/src/pages/ProjectPlanningDetail.jsx
 *
 * Entity-specific endpoint shapes (path, kind-vs-category form key, doc
 * payload schema) are kept in ENTITY_CONFIG below so when a generic
 * `POST /documents/upload` lands (next prompt), all three entries collapse
 * to one and parents don't have to change.
 *
 * Parents own the docs list state; this component just calls onChange with
 * the new full list after successful upload / delete.
 */
import React, { useRef, useState } from 'react';
import { Upload, FileText, Loader2, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api, BACKEND_URL } from '../lib/api';

const DEFAULT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.png,.jpg,.jpeg,.zip';

/**
 * Each entity type maps to the right backend route + doc payload adapter.
 * Adapter normalises backend doc shapes into:
 *   { _id, _categoryValue, _filename, _sizeBytes, _viewUrl? }
 * so the render code below stays entity-agnostic.
 */
const ENTITY_CONFIG = {
  enquiry: {
    upload: ({ entityId, file, categoryValue }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(
        `/enquiries/${entityId}/documents/upload?kind=${encodeURIComponent(categoryValue)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
    },
    delete: ({ entityId, docId }) =>
      api.delete(`/enquiries/${entityId}/documents/${docId}`),
    adapt: (d) => ({
      raw: d,
      _id: d.id,
      // Iter 165 — read path is now the unified store which uses `category`.
      // Fall back to legacy `kind` for any pre-refactor row still in memory.
      _categoryValue: d.category || d.kind,
      _filename: d.label || d.original_filename || d.filename,
      _sizeBytes: d.size || d.size_bytes || 0,
      _viewUrl: d.serve_url ? `${BACKEND_URL}${d.serve_url}` : null,
    }),
  },
  project: {
    upload: ({ entityId, file, categoryValue }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', categoryValue);
      return api.post(
        `/project-planning/plans/${entityId}/documents`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
    },
    delete: ({ entityId, docId }) =>
      api.delete(`/project-planning/plans/${entityId}/documents/${docId}`),
    adapt: (d) => ({
      raw: d,
      _id: d.id,
      _categoryValue: d.category,
      _filename: d.filename,
      _sizeBytes: d.size_bytes || 0,
      _viewUrl: null,
    }),
  },
  opportunity: {
    // Unified document lifecycle — POST /documents/opportunity/{id}/upload
    upload: ({ entityId, file, categoryValue }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', categoryValue);
      return api.post(
        `/documents/opportunity/${entityId}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
    },
    delete: ({ docId }) => api.delete(`/documents/${docId}`),
    adapt: (d) => ({
      raw: d,
      _id: d.id,
      _categoryValue: d.category || d.kind,
      _filename: d.filename || d.original_filename || d.label,
      _sizeBytes: d.size_bytes || d.size || 0,
      _viewUrl: d.serve_url ? `${BACKEND_URL}${d.serve_url}` : null,
    }),
  },
};

export default function DocumentUploader({
  entityType,
  entityId,
  documents = [],
  onChange,
  categories,
  defaultCategory,
  resolveEntityId,            // optional async () => entityId (used by Enquiry draft auto-save)
  disabled = false,
  accept = DEFAULT_ACCEPT,
  maxSizeMb = 30,
  testidPrefix = 'doc',
  title = 'Add Documents',
}) {
  const cfg = ENTITY_CONFIG[entityType];
  if (!cfg) throw new Error(`DocumentUploader: unknown entityType "${entityType}"`);
  if (!Array.isArray(categories) || categories.length === 0) {
    throw new Error('DocumentUploader: `categories` is required and must be non-empty');
  }

  const [category, setCategory] = useState(defaultCategory ?? categories[0].value);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const inputRef = useRef(null);

  const activeCategory = categories.find((c) => c.value === category) || categories[0];

  const resolveId = async () => {
    if (entityId) return entityId;
    if (resolveEntityId) return await resolveEntityId();
    return null;
  };

  const upload = async (files) => {
    if (!files || !files.length || disabled) return;
    const targetId = await resolveId();
    if (!targetId) return;

    setUploadingCategory(category);
    try {
      const uploaded = [];
      for (const file of files) {
        if ((file.size || 0) > maxSizeMb * 1024 * 1024) {
          toast.error(`${file.name} exceeds ${maxSizeMb} MB`);
          continue;
        }
        try {
          const r = await cfg.upload({ entityId: targetId, file, categoryValue: category });
          uploaded.push(r.data);
        } catch (e) {
          toast.error(`Upload failed for ${file.name}: ${e?.response?.data?.detail || e.message}`);
        }
      }
      if (uploaded.length) {
        toast.success(`${uploaded.length} ${activeCategory.label} document(s) uploaded`);
        onChange?.([...documents, ...uploaded]);
      }
    } finally {
      setUploadingCategory(null);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const targetId = await resolveId();
    if (!targetId) return;
    try {
      await cfg.delete({ entityId: targetId, docId });
      onChange?.(documents.filter((d) => d.id !== docId));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const adapted = documents.map((d) => cfg.adapt(d));
  const acceptHint = accept.replace(/\./g, '').toUpperCase().split(',').filter(Boolean).join(', ');

  return (
    <div
      className={`bg-white border border-slate-200 rounded-sm p-5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      data-testid={`${testidPrefix}-uploader`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-bold text-base">{title}</h4>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid={`${testidPrefix}-upload-category`}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white"
        >
          {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      <div
        className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors
          ${uploadingCategory ? 'border-amber-400 bg-amber-50/40' : 'border-slate-300 hover:border-[#3b4872]'}`}
        onClick={async () => {
          if (uploadingCategory) return;
          if (entityId) { inputRef.current?.click(); return; }
          if (resolveEntityId) {
            const id = await resolveEntityId();
            if (id) inputRef.current?.click();
          } else {
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (!uploadingCategory) upload(e.dataTransfer.files); }}
        data-testid={`${testidPrefix}-upload-zone`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={accept}
          onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
          data-testid={`${testidPrefix}-upload-input`}
        />
        {uploadingCategory ? (
          <>
            <Loader2 size={26} className="mx-auto text-amber-500 mb-2 animate-spin" />
            <div className="font-display font-bold text-sm text-amber-800">
              Uploading {categories.find((c) => c.value === uploadingCategory)?.label || uploadingCategory}…
            </div>
          </>
        ) : (
          <>
            <Upload size={26} className="mx-auto text-slate-400 mb-2" />
            <div className="font-display font-bold text-sm">
              Drop <span className={`px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase mx-0.5 ${activeCategory.colorClass}`}>{activeCategory.label}</span> files here, or click to browse
            </div>
            <div className="text-xs text-slate-500 mt-1">{acceptHint} · up to {maxSizeMb} MB each</div>
          </>
        )}
      </div>

      {adapted.length > 0 ? (
        <div className="mt-4 space-y-1.5" data-testid={`${testidPrefix}-doc-list`}>
          {adapted.map((d) => {
            const c = categories.find((x) => x.value === d._categoryValue) || categories[categories.length - 1];
            return (
              <div key={d._id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5">
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-display font-bold uppercase ${c.colorClass}`}>{c.label}</span>
                <FileText size={12} className="text-[#3b4872] shrink-0" />
                <span className="font-mono truncate flex-1" title={d._filename}>{d._filename}</span>
                <span className="text-slate-400 tabular-nums whitespace-nowrap">{(d._sizeBytes / 1024 / 1024).toFixed(2)} MB</span>
                {d._viewUrl && (
                  <a
                    href={`${d._viewUrl}?auth=${encodeURIComponent(localStorage.getItem('access_token') || '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#3b4872] hover:underline flex items-center gap-1 shrink-0"
                    data-testid={`${testidPrefix}-doc-view-${d._id}`}
                  >
                    <ExternalLink size={11} /> View
                  </a>
                )}
                <button
                  onClick={() => remove(d._id)}
                  className="text-slate-400 hover:text-rose-600 shrink-0"
                  data-testid={`${testidPrefix}-doc-del-${d._id}`}
                >
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-[11px] text-slate-400 uppercase tracking-widest font-display mt-3">
          No documents uploaded yet
        </p>
      )}
    </div>
  );
}

/* Convenience: standard category sets so parents don't re-declare them. */
export const ENQUIRY_DOC_CATEGORIES = [
  { value: 'BOQ',             label: 'BOQ',             colorClass: 'bg-rose-100 text-rose-700' },
  { value: 'Tender Document', label: 'Tender Document', colorClass: 'bg-amber-100 text-amber-700' },
  { value: 'GSS',             label: 'GSS',             colorClass: 'bg-violet-100 text-violet-700' },
  { value: 'SCC',             label: 'SCC',             colorClass: 'bg-violet-100 text-violet-700' },
  { value: 'Drawing',         label: 'Drawing',         colorClass: 'bg-sky-100 text-sky-700' },
  { value: 'Other',           label: 'Other',           colorClass: 'bg-slate-100 text-slate-700' },
];

export const OPP_DOC_CATEGORIES = [
  { value: 'NIT / RFQ',             label: 'NIT / RFQ',          colorClass: 'bg-amber-100 text-amber-700' },
  { value: 'Tender Document',        label: 'Tender Document',    colorClass: 'bg-rose-100 text-rose-700' },
  { value: 'BOQ',                    label: 'BOQ',                colorClass: 'bg-rose-100 text-rose-700' },
  { value: 'Drawings',               label: 'Drawings',           colorClass: 'bg-sky-100 text-sky-700' },
  { value: 'Specifications',         label: 'Specifications',     colorClass: 'bg-violet-100 text-violet-700' },
  { value: 'Eligibility Criteria',   label: 'Eligibility',        colorClass: 'bg-emerald-100 text-emerald-700' },
  { value: 'PQ Document',            label: 'PQ Document',        colorClass: 'bg-indigo-100 text-indigo-700' },
  { value: 'Client Correspondence',  label: 'Correspondence',     colorClass: 'bg-teal-100 text-teal-700' },
  { value: 'Other',                  label: 'Other',              colorClass: 'bg-slate-100 text-slate-700' },
];

export const PROJECT_PLANNING_DOC_CATEGORIES = [
  { value: 'boq',     label: 'BOQ',        colorClass: 'bg-rose-100 text-rose-700' },
  { value: 'drawing', label: 'Drawing',    colorClass: 'bg-sky-100 text-sky-700' },
  { value: 'scc',     label: 'SCC',        colorClass: 'bg-violet-100 text-violet-700' },
  { value: 'gcc',     label: 'GCC',        colorClass: 'bg-purple-100 text-purple-700' },
  { value: 'tender',  label: 'Tender Doc', colorClass: 'bg-amber-100 text-amber-700' },
  { value: 'other',   label: 'Other',      colorClass: 'bg-slate-100 text-slate-700' },
];
